import { useCallback, useEffect, useState } from 'react'
import { Banner, Heading, IconButton, Text, VStack } from 'wss3-forge'
import { Dismiss24Regular, Mic48Regular } from '@fluentui/react-icons'
import { useAuth } from '../contexts/AuthContext.js'
import { useVoiceSession, type VoiceState } from '../hooks/useVoiceSession.js'

export interface VoiceModeOverlayProps {
  open: boolean
  sessionId?: string | null
  onClose: () => void
  onTranscript?: (text: string) => void
  onResponse?: (text: string) => void
}

const STATE_LABEL: Record<VoiceState, string> = {
  closed: 'Déconnecté',
  connecting: 'Connexion...',
  idle: 'Je t’écoute. Parle quand tu veux.',
  listening: 'Je t’écoute...',
  thinking: 'Stark réfléchit...',
  speaking: 'Stark répond...',
  error: 'Erreur',
}

export function VoiceModeOverlay({
  open,
  sessionId,
  onClose,
  onTranscript,
  onResponse,
}: VoiceModeOverlayProps) {
  const { getValidAccessToken } = useAuth()
  const [transcript, setTranscript] = useState<string>('')
  const [response, setResponse] = useState<string>('')

  const handleTranscript = useCallback(
    (text: string) => {
      setTranscript(text)
      onTranscript?.(text)
    },
    [onTranscript],
  )

  const handleResponse = useCallback(
    (text: string) => {
      setResponse(text)
      onResponse?.(text)
    },
    [onResponse],
  )

  const voice = useVoiceSession({
    getToken: getValidAccessToken,
    sessionId: sessionId ?? null,
    onTranscript: handleTranscript,
    onResponse: handleResponse,
  })

  useEffect(() => {
    if (open) {
      void voice.open()
    } else {
      voice.close()
      setTranscript('')
      setResponse('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const isListening = voice.state === 'listening'
  const isSpeaking = voice.state === 'speaking'
  const isBusy = voice.state === 'thinking'

  // Visual: a single orb that pulses when listening, glows when speaking.
  const orbColor = isListening
    ? 'var(--color-success)'
    : isSpeaking
      ? 'var(--brand-primary)'
      : isBusy
        ? 'var(--text-muted)'
        : 'var(--bg-elevated)'
  const orbRingColor = isListening
    ? 'var(--color-success)'
    : isSpeaking
      ? 'var(--brand-primary)'
      : 'transparent'

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Mode voix"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        backgroundColor: 'var(--bg-primary)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'stark-voice-fade-in 200ms ease-out',
      }}
    >
      <style>{`
        @keyframes stark-voice-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes stark-orb-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 var(--orb-ring); }
          50%      { transform: scale(1.04); box-shadow: 0 0 0 28px transparent; }
        }
        @keyframes stark-orb-glow {
          0%, 100% { box-shadow: 0 0 28px 4px var(--orb-ring); }
          50%      { box-shadow: 0 0 60px 12px var(--orb-ring); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-stark-orb] { animation: none !important; }
        }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 'var(--spacing-md)' }}>
        <IconButton
          icon={<Dismiss24Regular />}
          tooltip="Fermer le mode voix"
          aria-label="Fermer le mode voix"
          variant="ghost"
          size="md"
          onClick={onClose}
        />
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--spacing-lg)',
        }}
      >
        <VStack gap="lg" align="center" style={{ maxWidth: 560, width: '100%' }}>
          <Heading level={3} style={{ textAlign: 'center', margin: 0 }}>
            {STATE_LABEL[voice.state]}
          </Heading>

          {voice.error && (
            <Banner variant="error" title="Erreur">
              {voice.error}
            </Banner>
          )}

          <div
            data-stark-orb
            // Custom CSS var so the keyframes can read the right ring color.
            style={
              {
                width: 168,
                height: 168,
                borderRadius: '50%',
                backgroundColor: orbColor,
                color: isListening || isSpeaking ? '#fff' : 'var(--text-primary)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 200ms ease, color 200ms ease',
                animation: isListening
                  ? 'stark-orb-pulse 1.4s ease-in-out infinite'
                  : isSpeaking
                    ? 'stark-orb-glow 2.2s ease-in-out infinite'
                    : undefined,
                ['--orb-ring' as string]: orbRingColor,
              } as React.CSSProperties
            }
          >
            <Mic48Regular />
          </div>

          <Text size="sm" color="muted" align="center">
            {isSpeaking
              ? 'Coupe-le en parlant, il s’arrête tout seul.'
              : isBusy
                ? '…'
                : 'Le micro reste ouvert. Reste silencieux pour conclure une phrase.'}
          </Text>

          {transcript && (
            <VStack gap="xs" align="center" style={{ width: '100%' }}>
              <Text size="xs" color="muted">Toi</Text>
              <Text style={{ textAlign: 'center', fontStyle: 'italic' }}>{transcript}</Text>
            </VStack>
          )}
          {response && (
            <VStack gap="xs" align="center" style={{ width: '100%' }}>
              <Text size="xs" color="muted">Stark</Text>
              <Text style={{ textAlign: 'center' }}>{response}</Text>
            </VStack>
          )}
        </VStack>
      </div>
    </div>
  )
}
